import { BankAccountPickerBC } from '@/components/filtros/BankAccountPickerBC';
import { AplicacionLineaDialog } from '@/components/propuestas-pago/AplicacionLineaDialog';
import { DisponibilidadCuentas } from '@/components/propuestas-pago/DisponibilidadCuentas';
import { EnviarABCDialog } from '@/components/propuestas-pago/EnviarABCDialog';
import { GenerarArchivoBNDialog } from '@/components/propuestas-pago/GenerarArchivoBNDialog';
import { GenerarArchivoBancoDialog } from '@/components/propuestas-pago/GenerarArchivoBancoDialog';
import { ReporteProveedoresDialog } from '@/components/propuestas-pago/ReporteProveedoresDialog';
import { SelectorFacturasPorProveedor } from '@/components/propuestas-pago/SelectorFacturasPorProveedor';
import { TotalesPropuestaBar } from '@/components/propuestas-pago/TotalesPropuestaBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import {
  useActualizarPropuestaPago,
  usePropuestaPago,
  useQuitarValidacionPropuestaPago,
  useRefrescarPropuestaPago,
  useValidarPropuestaPago,
} from '@/hooks/usePropuestasPago';
import { useUsuarioActual } from '@/hooks/useSolicitudesContado';
import { useVencimientosDetalle } from '@/hooks/useVencimientosDetalle';
import type { LineaCanasta } from '@/lib/PropuestaEnConstruccionContext';
import { resumenSaldoCuentas } from '@/lib/resumenCuentas';
import type {
  PropuestaPagoLineaDetalle,
  SaldoCuentaPropuesta,
  VencimientoDetalle,
} from '@adelante/shared';
import {
  ArrowLeft,
  ArrowsClockwise,
  CircleNotch,
  FilePdf,
  Info,
  LockSimple,
  Plus,
  SealCheck,
  Warning,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/** Convierte las líneas guardadas de la propuesta a un universo de "facturas" para reusar el selector. */
function lineasComoFacturas(lineas: PropuestaPagoLineaDetalle[]): VencimientoDetalle[] {
  return lineas.map((l) => ({
    entryNo: l.entryNo,
    vendorCode: l.vendorCode,
    vendorName: l.vendorName,
    documentNo: l.documentNo,
    externalDocumentNo: null,
    documentType: null,
    postingDate: null,
    dueDate: l.dueDate,
    paymentTermsCode: null,
    paymentMethodCode: null,
    vendorPostingGroup: null,
    currencyCode: l.currencyCode,
    remainingAmount: l.montoMaximo ?? l.montoAPagar,
    remainingAmtLCY:
      l.montoMaximo !== null && l.tasaImplicita !== null
        ? l.montoMaximo * l.tasaImplicita
        : l.montoAPagarLCY,
    daysToDue: null,
    bucketVencimiento: l.bucketVencimiento ?? 'Vencido',
    linkBC: l.linkBC,
    pactado: false,
  }));
}

function lineasComoCanasta(lineas: PropuestaPagoLineaDetalle[]): LineaCanasta[] {
  return lineas.map((l) => ({
    entryNo: l.entryNo,
    vendorCode: l.vendorCode,
    vendorName: l.vendorName,
    montoAPagar: l.montoAPagar,
    montoAPagarLCY: l.montoAPagarLCY,
    currencyCode: l.currencyCode,
    tasaImplicita: l.tasaImplicita ?? (l.montoAPagar !== 0 ? l.montoAPagarLCY / l.montoAPagar : 1),
    montoMaximo: l.montoMaximo ?? l.montoAPagar,
    cuentaBancoOrigen: l.cuentaBancoOrigen,
  }));
}

export function PropuestaPagoDetallePantalla() {
  const { data: yoData } = useUsuarioActual();
  const esAdmin = !!yoData?.data?.esAdmin;
  const puedePagar = !!yoData?.data?.puedePagar;
  const { id } = useParams<{ id: string }>();
  const idPropuesta = id ? Number(id) : null;
  const navigate = useNavigate();

  const { data, isLoading } = usePropuestaPago(idPropuesta);
  const actualizar = useActualizarPropuestaPago();
  const refrescar = useRefrescarPropuestaPago();
  const [recarga, setRecarga] = useState(0);
  const validar = useValidarPropuestaPago();
  const quitarValidacion = useQuitarValidacionPropuestaPago();
  const [reporteAbierto, setReporteAbierto] = useState(false);
  const { data: vencimientosData } = useVencimientosDetalle();
  const { data: cuentasBancoData } = useCuentasBancoBC();

  const [nombre, setNombre] = useState('');
  const [fechaPago, setFechaPago] = useState('');
  const [lineas, setLineas] = useState<PropuestaPagoLineaDetalle[]>([]);
  const [saldosCuentas, setSaldosCuentas] = useState<SaldoCuentaPropuesta[]>([]);
  const [cuentaParaTodas, setCuentaParaTodas] = useState<string | null>(null);
  const [dialogAgregarAbierto, setDialogAgregarAbierto] = useState(false);
  const [lineasAAgregar, setLineasAAgregar] = useState<LineaCanasta[]>([]);
  const [dialogArchivoBancoAbierto, setDialogArchivoBancoAbierto] = useState(false);
  const [dialogArchivoBNAbierto, setDialogArchivoBNAbierto] = useState(false);
  const [dialogEnviarBCAbierto, setDialogEnviarBCAbierto] = useState(false);

  const propuesta = data?.data;

  // biome-ignore lint/correctness/useExhaustiveDependencies: sincronizar el form solo cuando llegan datos nuevos de la propuesta, no en cada render
  useEffect(() => {
    if (propuesta) {
      setNombre(propuesta.nombre);
      setFechaPago(propuesta.fechaPagoPropuesta ?? '');
      setLineas(propuesta.lineas);
      setSaldosCuentas(propuesta.saldosCuentas);
    }
  }, [propuesta?.idPropuesta, recarga]);

  const facturasDeLaPropuesta = useMemo(() => lineasComoFacturas(lineas), [lineas]);
  const valorSelector = useMemo(() => lineasComoCanasta(lineas), [lineas]);

  const totalPorMoneda = useMemo(() => {
    const mapa = new Map<string, { total: number; totalLCY: number }>();
    for (const l of lineas) {
      const moneda = l.currencyCode || 'CRC';
      const actual = mapa.get(moneda) ?? { total: 0, totalLCY: 0 };
      mapa.set(moneda, {
        total: actual.total + l.montoAPagar,
        totalLCY: actual.totalLCY + l.montoAPagarLCY,
      });
    }
    return [...mapa.entries()].map(([currencyCode, v]) => ({ currencyCode, ...v }));
  }, [lineas]);

  const resumenCuentas = useMemo(
    () => resumenSaldoCuentas(lineas, saldosCuentas),
    [lineas, saldosCuentas],
  );

  const cuentasUsadas = useMemo(
    () => [...new Set(lineas.map((l) => l.cuentaBancoOrigen).filter((c): c is string => !!c))],
    [lineas],
  );

  // El archivo de banco se genera del lado del servidor a partir de lo que ya está guardado en
  // la base de datos — si hay cambios locales sin guardar (ej. una cuenta bancaria recién
  // asignada), generarlo ahora ignoraría justo esos cambios. Se compara contra la última
  // propuesta cargada del servidor para detectarlo.
  const hayCambiosSinGuardar = useMemo(() => {
    if (!propuesta) return false;
    if (nombre !== propuesta.nombre) return true;
    if ((fechaPago || null) !== propuesta.fechaPagoPropuesta) return true;
    if (JSON.stringify(saldosCuentas) !== JSON.stringify(propuesta.saldosCuentas)) return true;
    if (lineas.length !== propuesta.lineas.length) return true;
    const guardadasPorEntryNo = new Map(propuesta.lineas.map((l) => [l.entryNo, l]));
    return lineas.some((l) => {
      const guardada = guardadasPorEntryNo.get(l.entryNo);
      if (!guardada) return true;
      return (
        l.montoAPagar !== guardada.montoAPagar || l.cuentaBancoOrigen !== guardada.cuentaBancoOrigen
      );
    });
  }, [propuesta, nombre, fechaPago, saldosCuentas, lineas]);

  const detalleDisponible = vencimientosData?.data ?? [];
  const entryNosEnPropuesta = useMemo(() => new Set(lineas.map((l) => l.entryNo)), [lineas]);
  const facturasParaAgregar = useMemo(
    () => detalleDisponible.filter((f) => !entryNosEnPropuesta.has(f.entryNo)),
    [detalleDisponible, entryNosEnPropuesta],
  );

  function onChangeDesdeSelector(nuevas: LineaCanasta[]) {
    const porEntryNo = new Map(nuevas.map((n) => [n.entryNo, n]));
    setLineas((actual) =>
      actual
        .filter((l) => porEntryNo.has(l.entryNo))
        .map((l) => {
          const n = porEntryNo.get(l.entryNo);
          if (!n) return l;
          return {
            ...l,
            montoAPagar: n.montoAPagar,
            montoAPagarLCY: n.montoAPagarLCY,
            cuentaBancoOrigen: n.cuentaBancoOrigen,
          };
        }),
    );
  }

  function aplicarCuentaATodas() {
    if (!cuentaParaTodas) return;
    const cuenta = cuentasBancoData?.data?.find((c) => c.number === cuentaParaTodas);
    const monedaCuenta = cuenta?.currencyCode || 'CRC';
    let saltadas = 0;
    setLineas((actual) =>
      actual.map((l) => {
        if ((l.currencyCode || 'CRC') !== monedaCuenta) {
          saltadas++;
          return l;
        }
        return { ...l, cuentaBancoOrigen: cuentaParaTodas };
      }),
    );
    if (saltadas > 0) {
      toast.warning(
        `${saltadas} factura(s) en otra moneda no se asignaron a esa cuenta (${monedaCuenta}).`,
      );
    }
  }

  function confirmarAgregarFacturas() {
    setLineas((actual) => [
      ...actual,
      ...lineasAAgregar.map((l) => ({
        idLinea: 0,
        entryNo: l.entryNo,
        vendorCode: l.vendorCode,
        vendorName: l.vendorName,
        documentNo: null,
        dueDate: null,
        bucketVencimiento: null,
        montoAPagar: l.montoAPagar,
        montoAPagarLCY: l.montoAPagarLCY,
        currencyCode: l.currencyCode,
        cuentaBancoOrigen: l.cuentaBancoOrigen,
        notas: null,
        aplicacion: null,
        montoMaximo: l.montoMaximo,
        tasaImplicita: l.tasaImplicita,
        linkBC: null,
        estadoBC: 'VIGENTE' as const,
      })),
    ]);
    setLineasAAgregar([]);
    setDialogAgregarAbierto(false);
  }

  // Líneas cuyo pago no cayó en la factura de la propuesta: cerradas en BC y/o con la anotación de a
  // qué otra factura se aplicó. Se listan aparte (fuera del bloque bloqueado) para poder anotarlas.
  const [lineaAplicacion, setLineaAplicacion] = useState<PropuestaPagoLineaDetalle | null>(null);
  const lineasAclarar = useMemo(
    () => (propuesta?.lineas ?? []).filter((l) => l.estadoBC === 'CERRADA' || l.aplicacion),
    [propuesta],
  );
  const etiquetasLineas = useMemo(() => {
    const e: Record<number, { texto: string; titulo?: string; tono: 'ambar' | 'rojo' }> = {};
    for (const l of propuesta?.lineas ?? []) {
      if (l.aplicacion) {
        e[l.entryNo] = {
          texto: l.aplicacion.aplicadaADocumento
            ? `No aplicada · pago a ${l.aplicacion.aplicadaADocumento}`
            : 'No aplicada a esta factura',
          titulo: [
            l.aplicacion.nota,
            l.aplicacion.ajustadaPor && `Anotado por ${l.aplicacion.ajustadaPor}`,
          ]
            .filter(Boolean)
            .join(' · '),
          tono: 'ambar',
        };
      } else if (l.estadoBC === 'CERRADA') {
        e[l.entryNo] = {
          texto: 'Cerrada en BC',
          titulo: 'La factura ya se cerró en BC: indicá a qué factura se aplicó el pago',
          tono: 'rojo',
        };
      }
    }
    return e;
  }, [propuesta]);

  const lineasDesactualizadas = useMemo(
    () => (propuesta?.lineas ?? []).filter((l) => l.estadoBC !== 'VIGENTE'),
    [propuesta],
  );
  const cerradas = lineasDesactualizadas.filter((l) => l.estadoBC === 'CERRADA').length;
  const saldoMenor = lineasDesactualizadas.length - cerradas;

  function refrescarDesdeBC() {
    if (!idPropuesta) return;
    refrescar.mutate(
      { id: idPropuesta },
      {
        onSuccess: (res) => {
          setRecarga((n) => n + 1);
          const r = res.data;
          toast.success(
            r
              ? `Propuesta actualizada: ${r.lineasEliminadas} factura(s) quitadas, ${r.lineasAjustadas} ajustada(s) al saldo actual`
              : 'Propuesta actualizada',
          );
        },
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : 'No se pudo actualizar la propuesta'),
      },
    );
  }

  function validarPropuesta() {
    if (!idPropuesta) return;
    validar.mutate(idPropuesta, {
      onSuccess: () => toast.success('Propuesta validada: los datos quedaron congelados'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo validar'),
    });
  }

  function quitarValidacionPropuesta() {
    if (!idPropuesta) return;
    quitarValidacion.mutate(idPropuesta, {
      onSuccess: () => toast.success('Validación quitada: la propuesta se puede editar de nuevo'),
      onError: (e) =>
        toast.error(e instanceof Error ? e.message : 'No se pudo quitar la validación'),
    });
  }

  function guardar() {
    if (!idPropuesta || lineas.length === 0) return;
    actualizar.mutate(
      {
        id: idPropuesta,
        data: {
          nombre,
          fechaPagoPropuesta: fechaPago || null,
          lineas: lineas.map((l) => ({
            entryNo: l.entryNo,
            vendorCode: l.vendorCode,
            montoAPagar: l.montoAPagar,
            montoAPagarLCY: l.montoAPagarLCY,
            currencyCode: l.currencyCode,
            cuentaBancoOrigen: l.cuentaBancoOrigen,
            notas: l.notas,
          })),
          saldosCuentas,
        },
      },
      {
        onSuccess: () => toast.success('Propuesta guardada'),
        onError: () => toast.error('Error al guardar'),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando…
      </div>
    );
  }

  if (!propuesta) {
    return <p className="text-sm text-muted-foreground">Propuesta no encontrada.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/vencimientos/propuestas')}>
          <ArrowLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{propuesta.nombre}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vista previa — todavía no se escribe nada en Business Central.
          </p>
        </div>
      </div>

      {lineasDesactualizadas.length > 0 && (
        <div className="flex items-start justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <Warning className="mt-0.5 h-4 w-4 shrink-0" weight="bold" aria-hidden="true" />
            <div>
              <p className="font-medium">
                {lineasDesactualizadas.length} factura(s) cambiaron en BC desde que se creó esta
                propuesta
              </p>
              <p>
                {cerradas > 0 && `${cerradas} ya están cerradas (pagadas). `}
                {saldoMenor > 0 && `${saldoMenor} tienen un saldo menor al monto a pagar. `}
                {propuesta.bloqueada
                  ? 'La propuesta está bloqueada, no se puede actualizar: eliminala y creá una nueva si hace falta.'
                  : 'Actualizá la propuesta para quitarlas / ajustarlas al saldo actual.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {propuesta.bloqueada && (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-4 text-sm">
          <LockSimple className="mt-0.5 h-4 w-4 shrink-0" weight="bold" aria-hidden="true" />
          <div>
            <p className="font-medium">
              Propuesta bloqueada
              {propuesta.motivoBloqueo === 'ENVIADA_A_BC' && ' — ya se envió a BC'}
              {propuesta.motivoBloqueo === 'ARCHIVO_BANCO' && ' — ya se generó el archivo de banco'}
              {propuesta.motivoBloqueo === 'VALIDADA' && ' — validada'}
            </p>
            <p className="text-muted-foreground">
              No se pueden cambiar facturas, montos ni cuentas.{' '}
              {propuesta.motivoBloqueo === 'VALIDADA'
                ? 'Quitá la validación para editarla.'
                : 'Para rehacerla, eliminá la propuesta (si se envió a BC, se quita del diario) y creá una nueva.'}
              {propuesta.validadaEn &&
                ' Los datos están congelados: ya se puede emitir el reporte.'}
            </p>
          </div>
        </div>
      )}

      <TotalesPropuestaBar totales={totalPorMoneda} resumenCuentas={resumenCuentas} />

      {(lineasAclarar.length > 0 || (puedePagar && !!propuesta.bloqueada)) && (
        <Card className="border-amber-300">
          <CardHeader>
            <CardTitle className="text-base">
              Pagos que no cayeron en la factura de la propuesta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              Si el pago de una factura salió igual pero en BC se aplicó a otra (por ejemplo porque
              la factura ya se había cerrado), anota a qué factura se aplicó: queda como etiqueta en
              la propuesta y en el reporte del proveedor. No cambia nada en Business Central.
            </p>
            <ul className="divide-y">
              {lineasAclarar.map((l) => (
                <li
                  key={l.entryNo}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <div>
                    <span className="font-medium">{l.vendorName ?? l.vendorCode}</span>
                    <span className="ml-2 font-mono text-xs">{l.documentNo}</span>
                    <div className="text-xs text-muted-foreground">
                      {l.aplicacion
                        ? `Aplicado a ${l.aplicacion.aplicadaADocumento ?? '(sin indicar)'}${l.aplicacion.nota ? ` — ${l.aplicacion.nota}` : ''}`
                        : 'Cerrada en BC: falta indicar a qué factura se aplicó el pago'}
                    </div>
                  </div>
                  {puedePagar && (
                    <Button size="sm" variant="outline" onClick={() => setLineaAplicacion(l)}>
                      {l.aplicacion ? 'Editar' : 'Indicar aplicación'}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {puedePagar && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Label htmlFor="anotar-otra" className="text-xs text-muted-foreground">
                  Anotar otra factura:
                </Label>
                <select
                  id="anotar-otra"
                  className="h-8 max-w-md rounded-md border border-input bg-background px-2 text-xs"
                  value=""
                  onChange={(e) => {
                    const l = (propuesta.lineas ?? []).find(
                      (x) => x.entryNo === Number(e.target.value),
                    );
                    if (l) setLineaAplicacion(l);
                  }}
                >
                  <option value="">Elegí la factura…</option>
                  {(propuesta.lineas ?? []).map((l) => (
                    <option key={l.entryNo} value={l.entryNo}>
                      {(l.vendorName ?? l.vendorCode).slice(0, 36)} — {l.documentNo}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <fieldset disabled={propuesta.bloqueada} className="m-0 min-w-0 space-y-6 border-0 p-0">
        <Card>
          <CardHeader>
            <CardTitle>Datos de la propuesta</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Fecha de pago propuesta</Label>
              <Input type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Aplicar cuenta bancaria a todas las líneas</Label>
              <div className="flex gap-2">
                <BankAccountPickerBC value={cuentaParaTodas} onChange={setCuentaParaTodas} />
                <Button type="button" variant="outline" onClick={aplicarCuentaATodas}>
                  Aplicar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Facturas de la propuesta</CardTitle>
            <Button variant="outline" onClick={() => setDialogAgregarAbierto(true)}>
              <Plus className="h-4 w-4" weight="bold" aria-hidden="true" />
              Agregar facturas
            </Button>
          </CardHeader>
          <CardContent>
            <SelectorFacturasPorProveedor
              facturas={facturasDeLaPropuesta}
              etiquetas={etiquetasLineas}
              value={valorSelector}
              onChange={onChangeDesdeSelector}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Disponibilidad de cuentas</CardTitle>
          </CardHeader>
          <CardContent>
            <DisponibilidadCuentas
              lineas={lineas}
              saldos={saldosCuentas}
              onChange={setSaldosCuentas}
            />
          </CardContent>
        </Card>
      </fieldset>

      <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-muted/30 p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4" weight="bold" aria-hidden="true" />
          {hayCambiosSinGuardar
            ? 'Tenés cambios sin guardar — guardalos antes de generar el archivo de banco.'
            : esAdmin
              ? 'Enviar a BC deja los pagos en el Diario de pagos sin registrar — Contabilidad los revisa y registra.'
              : 'Contabilidad: puedes elegir la cuenta de origen, generar los archivos de banco y enviar a BC. Qué se paga y cuánto lo define administración.'}
        </div>
        <div className="flex gap-2">
          {puedePagar && (
            <Button
              variant="outline"
              onClick={() => setDialogArchivoBancoAbierto(true)}
              disabled={cuentasUsadas.length === 0 || hayCambiosSinGuardar}
              title={
                hayCambiosSinGuardar
                  ? 'Guardá los cambios primero — el archivo se genera con lo último guardado'
                  : cuentasUsadas.length === 0
                    ? 'Asigná una cuenta bancaria a alguna línea primero'
                    : undefined
              }
            >
              Generar archivo de banco
            </Button>
          )}
          {puedePagar && (
            <Button
              variant="outline"
              onClick={() => setDialogArchivoBNAbierto(true)}
              disabled={cuentasUsadas.length === 0 || hayCambiosSinGuardar}
              title={
                hayCambiosSinGuardar
                  ? 'Guardá los cambios primero — el archivo se genera con lo último guardado'
                  : cuentasUsadas.length === 0
                    ? 'Asigná una cuenta bancaria a alguna línea primero'
                    : 'Archivo .SIN para subir al Banco Nacional'
              }
            >
              Archivo Banco Nacional
            </Button>
          )}
          {puedePagar && (
            <Button
              variant="outline"
              onClick={refrescarDesdeBC}
              disabled={propuesta.bloqueada || hayCambiosSinGuardar || refrescar.isPending}
              title={
                propuesta.bloqueada
                  ? 'La propuesta está bloqueada: eliminala y creá una nueva'
                  : hayCambiosSinGuardar
                    ? 'Guardá los cambios primero'
                    : 'Quitar las facturas ya cerradas en BC y ajustar los saldos'
              }
            >
              <ArrowsClockwise className="h-4 w-4" weight="bold" aria-hidden="true" />
              {refrescar.isPending ? 'Actualizando...' : 'Actualizar desde BC'}
            </Button>
          )}
          <Button
            onClick={guardar}
            disabled={propuesta.bloqueada || actualizar.isPending || lineas.length === 0}
            title={propuesta.bloqueada ? 'La propuesta está bloqueada' : undefined}
          >
            {actualizar.isPending ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          {puedePagar && (
            <Button
              variant="outline"
              onClick={() => setDialogEnviarBCAbierto(true)}
              disabled={cuentasUsadas.length === 0 || hayCambiosSinGuardar}
              title={
                hayCambiosSinGuardar
                  ? 'Guardá los cambios primero — se envía lo último guardado'
                  : cuentasUsadas.length === 0
                    ? 'Asigná una cuenta bancaria a alguna línea primero'
                    : 'Dejar los pagos en el Diario de pagos de Business Central (sin registrar)'
              }
            >
              Enviar a BC
            </Button>
          )}
          {propuesta.validadaEn ? (
            <>
              <Button onClick={() => setReporteAbierto(true)}>
                <FilePdf className="h-4 w-4" weight="bold" aria-hidden="true" />
                Reporte proveedores
              </Button>
              {propuesta.motivoBloqueo === 'VALIDADA' && (
                <Button
                  variant="outline"
                  onClick={quitarValidacionPropuesta}
                  disabled={quitarValidacion.isPending}
                >
                  Quitar validación
                </Button>
              )}
            </>
          ) : puedePagar ? (
            <Button
              variant="outline"
              onClick={validarPropuesta}
              disabled={hayCambiosSinGuardar || validar.isPending || lineas.length === 0}
              title={
                hayCambiosSinGuardar
                  ? 'Guardá los cambios primero'
                  : 'Congela los datos de la propuesta (ya no se podrá editar) y habilita el reporte para proveedores'
              }
            >
              <SealCheck className="h-4 w-4" weight="bold" aria-hidden="true" />
              {validar.isPending ? 'Validando...' : 'Validar propuesta'}
            </Button>
          ) : null}
        </div>
      </div>

      {idPropuesta && (
        <AplicacionLineaDialog
          idPropuesta={idPropuesta}
          linea={lineaAplicacion}
          onClose={() => setLineaAplicacion(null)}
        />
      )}

      {idPropuesta && (
        <EnviarABCDialog
          idPropuesta={idPropuesta}
          cuentasUsadas={cuentasUsadas}
          open={dialogEnviarBCAbierto}
          onClose={() => setDialogEnviarBCAbierto(false)}
        />
      )}

      {idPropuesta && (
        <GenerarArchivoBancoDialog
          idPropuesta={idPropuesta}
          cuentasUsadas={cuentasUsadas}
          open={dialogArchivoBancoAbierto}
          onClose={() => setDialogArchivoBancoAbierto(false)}
        />
      )}

      {idPropuesta && (
        <GenerarArchivoBNDialog
          idPropuesta={idPropuesta}
          cuentasUsadas={cuentasUsadas}
          open={dialogArchivoBNAbierto}
          onClose={() => setDialogArchivoBNAbierto(false)}
        />
      )}

      {idPropuesta && (
        <ReporteProveedoresDialog
          idPropuesta={idPropuesta}
          open={reporteAbierto}
          onClose={() => setReporteAbierto(false)}
        />
      )}

      <Dialog open={dialogAgregarAbierto} onOpenChange={setDialogAgregarAbierto}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Agregar facturas a la propuesta</DialogTitle>
            <DialogDescription className="sr-only">
              Elegí facturas adicionales de cualquier proveedor para incluir en esta propuesta.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <SelectorFacturasPorProveedor
              facturas={facturasParaAgregar}
              value={lineasAAgregar}
              onChange={setLineasAAgregar}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setLineasAAgregar([]);
                setDialogAgregarAbierto(false);
              }}
            >
              Cancelar
            </Button>
            <Button disabled={lineasAAgregar.length === 0} onClick={confirmarAgregarFacturas}>
              Agregar {lineasAAgregar.length} factura(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
